import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        await morphSection(sectionId, sectionHTML, mode);
      }

      return sectionHTML;
    } catch (err) {
      console.warn('[sectionRenderer] renderSection failed', sectionId, err);
      return '';
    } finally {
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Section Rendering API can return 429/503 under `shopify theme dev` or burst traffic; backoff and retry.
   * @param {string} sectionUrl
   * @returns {Promise<string>}
   */
  async #fetchSectionTextWithRetry(sectionUrl) {
    const maxAttempts = 5;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await fetch(sectionUrl);
      if (response.status === 429 || response.status === 503) {
        const ra = response.headers.get('Retry-After');
        let delayMs = 350 * 2 ** attempt;
        if (ra) {
          const sec = parseInt(ra, 10);
          if (!Number.isNaN(sec)) delayMs = Math.min(sec * 1000, 12000);
        }
        delayMs = Math.min(delayMs, 12000);
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      if (!response.ok) {
        console.warn('[sectionRenderer] Section fetch HTTP', response.status, sectionUrl);
        return '';
      }
      return response.text();
    }
    const last = await fetch(sectionUrl);
    if (!last.ok) {
      console.warn('[sectionRenderer] Section fetch HTTP', last.status, sectionUrl);
      return '';
    }
    return last.text();
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    if (!normalizeSectionId(sectionId)) {
      console.warn('[sectionRenderer] getSectionHTML: missing or invalid section id');
      return '';
    }

    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = this.#fetchSectionTextWithRetry(sectionUrl);

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  const normalized = normalizeSectionId(sectionId);
  if (normalized) {
    url.searchParams.set('section_id', normalized);
  }
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  if (sectionId == null || sectionId === '') return '';
  return String(sectionId).replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {'hydration'|'full'} [mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 */
export async function morphSection(sectionId, html, mode = 'full', options = {}) {
  try {
    if (html == null || (typeof html === 'string' && html.trim() === '')) {
      return;
    }

    const { injectStylesheet = false } = options;
    const htmlString = typeof html === 'string' ? html : String(html);
    const fragment = new DOMParser().parseFromString(htmlString, 'text/html');
    const existingElement = document.getElementById(buildSectionSelector(sectionId));
    const newElement = fragment.getElementById(buildSectionSelector(sectionId));

    if (!existingElement) {
      console.warn(`[morphSection] Section "${sectionId}" not in document; skipping morph (navigation or missing section).`);
      return;
    }

    if (!newElement) {
      console.warn(
        `[morphSection] Section "${sectionId}" missing in response HTML; skipping morph (stale or partial response).`
      );
      return;
    }

    morph(existingElement, newElement, {
      ...MORPH_OPTIONS,
      hydrationMode: mode === 'hydration',
    });

    if (injectStylesheet) {
      injectSectionStylesheet(fragment, existingElement);
    }
  } catch (err) {
    console.warn(`[morphSection] Failed for section "${sectionId}"`, err);
  }
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {Document} fragment - The parsed response document
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
